import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuthOptions } from "@/hooks/use-auth-options";
import { useQuickLogin } from "@/hooks/use-quick-login";
import { signIn } from "@/lib/auth-client";
import { forgetGuardSession } from "@/lib/route-guards";

const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

type LoginValues = z.infer<typeof loginSchema>;

export function LoginForm() {
  const router = useRouter();
  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });
  const signUpOpen = useAuthOptions().data?.signUp === true;
  const quickLogins = useQuickLogin().data ?? [];

  async function onSubmit(values: LoginValues) {
    const { error } = await signIn.email({
      email: values.email,
      password: values.password,
    });

    if (error) {
      toast.error(error.message ?? "Failed to sign in");
      return;
    }

    // The guards may still hold the previous user, or a signed-out answer, for 30 seconds.
    forgetGuardSession();
    router.navigate({ to: "/dashboard" });
  }

  return (
    <Card className="border-border/60 shadow-sm">
      <CardHeader className="space-y-1 text-center pb-2">
        <h1 className="font-display text-2xl font-bold">Sign in with your email</h1>
        <p className="text-muted-foreground text-sm">Use the account your admin set up for you</p>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="you@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="••••••••" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              size="lg"
              className="w-full rounded-[5px] font-semibold"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </Form>
        {/* TEMPORARY, sandbox only (src/lib/quick-login.ts): fill in a seed account. */}
        {quickLogins.length > 0 && (
          <div className="mt-6 space-y-2 rounded-md border border-dashed p-3">
            <p className="text-center text-xs text-muted-foreground">
              Sandbox — fill in a test login
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {quickLogins.map((account) => (
                <Button
                  key={account.email}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    form.setValue("email", account.email, { shouldValidate: true });
                    form.setValue("password", account.password, { shouldValidate: true });
                  }}
                >
                  {account.label}
                </Button>
              ))}
            </div>
          </div>
        )}
        {/* Only when the app lets people make their own account (ALLOW_SIGNUP). */}
        {signUpOpen && (
          <p className="text-center text-sm text-muted-foreground mt-6">
            Don't have an account?{" "}
            <a href="/signup" className="text-primary hover:underline underline-offset-4">
              Sign up
            </a>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
